import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search, Crown, Mail, Phone, Calendar, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CRM = () => {
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'total_visits', direction: 'desc' });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (search = "") => {
    setLoading(true);
    try {
      const url = search ? `${API}/customers?search=${encodeURIComponent(search)}` : `${API}/customers`;
      const response = await axios.get(url);
      
      // Apply initial sort after fetching
      const sortedData = sortData(response.data, sortConfig.key, sortConfig.direction);
      setCustomers(sortedData);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const sortData = (data, key, direction) => {
    const sorted = [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Handle null/undefined - put at end
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // For numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // For strings - case insensitive
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
    
    return sorted;
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    fetchCustomers(query);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
    const sortedCustomers = sortData(customers, key, direction);
    setCustomers(sortedCustomers);
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 text-[#A1A1AA]" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-[#22C55E]" />
      : <ArrowDown className="w-4 h-4 text-[#22C55E]" />;
  };

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-[#27272A] bg-[#18181B]/80 backdrop-blur-md sticky top-0 z-10">
        <div className="px-8 py-6">
          <h1 className="text-4xl font-bold tracking-tight font-mono mb-2">Client CRM</h1>
          <p className="text-sm text-[#A1A1AA]">Customer relationship management</p>

          <div className="mt-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 bg-[#09090B] border-[#27272A] focus:ring-[#22C55E] focus:border-[#22C55E]"
              />
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto scroll-container p-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#22C55E]"></div>
          </div>
        ) : customers.length === 0 ? (
          <div data-testid="no-customers" className="flex flex-col items-center justify-center h-64 glass-card rounded-lg">
            <Search className="w-16 h-16 text-[#27272A] mb-4" />
            <p className="text-lg text-[#A1A1AA]">No customers found</p>
          </div>
        ) : (
          <div className="glass-card rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#09090B] border-b border-[#27272A] sticky top-0">
                <tr>
                  <th 
                    onClick={() => handleSort('customer_name')}
                    className="px-6 py-4 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider cursor-pointer hover:text-[#22C55E] transition-colors select-none"
                  >
                    <div className="flex items-center gap-2">
                      Customer
                      {getSortIcon('customer_name')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('phone')}
                    className="px-6 py-4 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider cursor-pointer hover:text-[#22C55E] transition-colors select-none"
                  >
                    <div className="flex items-center gap-2">
                      Phone
                      {getSortIcon('phone')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('email')}
                    className="px-6 py-4 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider cursor-pointer hover:text-[#22C55E] transition-colors select-none"
                  >
                    <div className="flex items-center gap-2">
                      Email
                      {getSortIcon('email')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('total_visits')}
                    className="px-6 py-4 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider cursor-pointer hover:text-[#22C55E] transition-colors select-none"
                  >
                    <div className="flex items-center gap-2">
                      Total Visits
                      {getSortIcon('total_visits')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('last_visit')}
                    className="px-6 py-4 text-left text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider cursor-pointer hover:text-[#22C55E] transition-colors select-none"
                  >
                    <div className="flex items-center gap-2">
                      Last Visit
                      {getSortIcon('last_visit')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, index) => {
                  // Create unique key combining phone and index
                  const uniqueKey = `${customer.phone || 'unknown'}-${index}`;
                  
                  return (
                    <tr
                      key={uniqueKey}
                      data-testid={`customer-row-${index}`}
                      className="border-b border-[#27272A]/50 hover:bg-[#27272A]/30 transition-colors"
                    >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#22C55E]/20 flex items-center justify-center font-mono font-bold text-[#22C55E]">
                          {customer.customer_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold">{customer.customer_name}</p>
                          {customer.total_visits >= 3 && (
                            <Badge data-testid={`vip-badge-${index}`} className="vip-badge text-xs mt-1">
                              <Crown className="w-3 h-3 mr-1" />
                              VIP
                            </Badge>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Phone className="w-4 h-4" />
                        <span className="font-mono text-sm">{customer.phone}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Mail className="w-4 h-4" />
                        <span className="text-sm">{customer.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span data-testid={`visits-${index}`} className="font-mono text-lg font-bold text-[#3B82F6]">{customer.total_visits}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Calendar className="w-4 h-4" />
                        <span className="font-mono text-sm">{customer.last_visit}</span>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CRM;